import Vue from 'vue'
import Vuex from 'vuex'
import createUsersModule from './users'
import createArticlesModule from './articles'
import createBlocksModule from './blocks'
import createBlogArticlesModule from './blog-articles'
import createBlogCategoriesModule from './blog-categories'
import createFilesModule from './files'
import createOpportunitiesModule from './opportunities'
import createOpportunityFilterModule from './opportunity-filter'
import createOpportunityTypesModule from './opportunity-types'
import createContactsModule from './contacts'
import createCompaniesModule from './companies'
import createProfilesModule from './profiles'
import createMetasModule from './metas'
import createContentModule from './content'
import createCommunitiesModule from './communities'
import createMetadataModule from './meta/metadata'
import createLearnModule from './learn'
import createLearnSourcesModule from './learn/learn-source'
import createLearnSourceGroupsModule from './learn/learn-source-group'
import createLearnContentTypesModule from './learn/learn-content-types'

import ArticleService from '@/services/ArticleService'
import OpportunityService from '@/services/OpportunityService'
import ProfileService from '@/services/ProfileService'

Vue.use(Vuex)
export function createStore(router) {
  const store = new Vuex.Store({
    modules: {
      articles: createArticlesModule(),
      blocks: createBlocksModule(),
      blogArticles: createBlogArticlesModule(),
      blogCategories: createBlogCategoriesModule(),
      contacts: createContactsModule(),
      companies: createCompaniesModule(),
      files: createFilesModule(),
      opportunities: createOpportunitiesModule(),
      opportunityFilter: createOpportunityFilterModule(),
      opportunityTypes: createOpportunityTypesModule(),
      profiles: createProfilesModule(),
      users: createUsersModule(),
      metas: createMetasModule(),
      content: createContentModule(),
      communities: createCommunitiesModule(),
      metadata: createMetadataModule(),
      learn: createLearnModule(),
      learnSources: createLearnSourcesModule(),
      learnSourceGroups: createLearnSourceGroupsModule(),
      learnContentTypes: createLearnContentTypesModule()
    },
    state() {
      return {
        user: null,
        profile: {},
        userPromise: null,
        article: null,
        opportunity: null,
        opportunityState: 'initial',
        backendError: false
      }
    },
    actions: {
      async getProfile({ commit }, { user }) {
        const { profile } = await ProfileService.getProfile(user.email)

        commit('SET_PROFILE', { profile })
      },
      async getArticle({ commit, getters }, slug) {
        commit('SET_BACKEND_ERROR', false)
        commit('SET_ARTICLE', { article: null })
        try {
          commit('SET_ARTICLE', await ArticleService.getArticle(slug))
        } catch (e) {
          if (e.response && e.response.status === 404) {
            router.push({ path: '/404' })
          } else {
            commit('SET_BACKEND_ERROR', true)
          }
        }
      },
      async getOpportunity({ commit, getters }, { opportunityId }) {
        commit('SET_BACKEND_ERROR', false)
        commit('SET_OPPORTUNITY', { opportunity: null })
        try {
          commit('SET_OPPORTUNITY', await OpportunityService.getOpportunity(opportunityId))
          commit('SET_OPPORTUNITY_STATE', { opportunityState: 'initialized' })
        } catch (e) {
          if (e.response && e.response.status === 404) {
            if (/job-opening/.test(router.currentRoute.path)) {
              router.push({ path: '/job-board' })
            } else {
              router.push({ path: '/404' })
            }
          } else {
            commit('SET_BACKEND_ERROR', true)
          }
          commit('SET_BACKEND_ERROR', true)
        }
      },
      async applyOpportunity({ commit, state }) {
        try {
          commit('SET_OPPORTUNITY_STATE', { opportunityState: 'pending' })
          commit('APPLY_OPPORTUNITY', { opportunity: state.opportunity.slug })
        } catch (e) {
          commit('SET_BACKEND_ERROR', true)
        }
      }
    },
    mutations: {
      SET_USER: (state, { user }) => {
        state.user = user || null
      },
      SET_PROFILE: (state, { profile }) => {
        state.profile = profile || {}
      },
      SET_USER_PROMISE: (state, { userPromise }) => {
        state.userPromise = userPromise
      },
      SET_ARTICLE: (state, { article }) => {
        state.article = article
      },
      SET_OPPORTUNITY: (state, { opportunity }) => {
        state.opportunity = opportunity
      },
      SET_OPPORTUNITY_STATE: (state, { opportunityState }) => {
        state.opportunityState = opportunityState
      },
      APPLY_OPPORTUNITY: async (state, { opportunity }) => {
        try {
          await OpportunityService.applyOpportunity(opportunity)
          state.opportunityState = 'applied'
        } catch (err) {
          state.opportunityState = 'error'
        }
      },
      SET_BACKEND_ERROR: (state, error) => {
        state.backendError = error
      }
    },
    getters: {
      user: (state) => state.user,
      profile: (state) => state.profile,
      userPromise: (state) => {
        return new Promise((resolve) => {
          state.userPromise.then(() => {
            resolve(state.user)
          })
        })
      },
      loggedIn: (state) => {
        return state.user !== null
      },
      article: (state) => state.article,
      opportunity: (state) => state.opportunity,
      opportunityState: (state) => state.opportunityState,
      backendError: (state) => state.backendError
    }
  })

  return store
}

export default createStore()
